import isEqual from 'lodash.isequal';
import { diff } from 'deep-object-diff';
import { retry, createApi } from '@reduxjs/toolkit/query/react';
import { QueueTab, ActionQueue, ActionQueueType } from '@gv/triage-components';
import {
  createSlice,
  PayloadAction,
  createEntityAdapter,
} from '@reduxjs/toolkit';

import { URL } from 'api/constants';
import { apiQuery } from 'store/query';
import { TransferMergeFormValues } from 'types';
import { login, isLogoutAction } from 'store/slices/auth';
import { getUser, clearCaseOnCallPopupOpened } from 'utils/storage';
import {
  getQueueItemId,
  jsonSortedKeysReplacer,
  getActionCenterCaseForm,
} from 'utils/helpers';

import { booleanFormFields, fillSavedFormIfExist } from '../cases/thunks';

import { isSame, selectActionQueueFromStore } from './selectors';
import {
  AddTask,
  EndTask,
  UpdateTask,
  UpdateCaseForm,
  SetOnHoldAction,
  SMSBatchPayload,
  ActionQueueState,
  PetOwnerTemplate,
  TaskIsLoadingProps,
  ActionQueueResponse,
  MessageRecipientType,
  UpdateCallFlowFormPayload,
  SetClientInProgressUpdate,
} from './types';

const selectId = (item: ActionQueue) => getQueueItemId(item);

const actionQueueAdapter = createEntityAdapter<ActionQueue, string>({
  selectId,
});

const initialState: ActionQueueState = {
  ...actionQueueAdapter.getInitialState(),
  taskForms: {},
  currentTasks: [],
  loadingTasks: [],
  callFlowForms: {},
  isTaskSwitching: false,
  clientSetInProgress: [],
  receiverId: getUser()?.id,
};

const retryQuery = retry(apiQuery, {
  maxRetries: 5,
});

export const actionQueueApi = createApi({
  baseQuery: retryQuery,
  reducerPath: 'actionQueueApi',
  endpoints: (build) => ({
    list: build.query<ActionQueue[], void>({
      query: () => ({
        method: 'get',
        url: URL.GET_ACTION_QUEUE,
      }),
      transformResponse: (response: ActionQueueResponse) => {
        const result = response.data.data;
        return result;
      },
    }),
  }),
});

let oldQueue: ActionQueue[] = [];
let oldVisibleQueue: ActionQueue[] = [];

const logQueuePayload = (state: ActionQueueState, payload: ActionQueue[]) => {
  if (!isEqual(oldQueue, payload)) {
    let payloadData = JSON.parse(
      JSON.stringify(payload, jsonSortedKeysReplacer)
    );
    console.log('New Queue', {
      queue: payloadData,
      diff: diff(oldQueue, payloadData),
    });
    oldQueue = payloadData;
  }
};

const logQueueChanges = (state: ActionQueueState) => {
  let newVisible: ActionQueue[] = [];
  Object.values(QueueTab).forEach((tab) => {
    newVisible = newVisible.concat(
      selectActionQueueFromStore(state, tab).map((item) =>
        JSON.parse(JSON.stringify(item), jsonSortedKeysReplacer)
      )
    );
  });

  if (!isEqual(oldVisibleQueue, newVisible)) {
    console.log('New Local Queue', {
      queue: newVisible,
      diff: diff(oldVisibleQueue, newVisible),
      diffWithResponse: diff(oldQueue, newVisible),
    });
    oldVisibleQueue = newVisible;
  }
};

export const actionQueueSlice = createSlice({
  initialState,
  name: 'actionQueue',
  extraReducers: (builder) => {
    builder.addCase(login.fulfilled, (state, action) => {
      const { user } = action.payload?.data;
      state.receiverId = user?.id;
    });
    builder.addMatcher(
      actionQueueApi.endpoints.list.matchFulfilled,
      (state, { payload }) => {
        logQueuePayload(state, payload);

        actionQueueAdapter.setAll(state, payload);
        const { receiverId, currentTasks } = state;

        if (currentTasks.length) {
          state.currentTasks = currentTasks.map((currentTask) => {
            const update = payload.find((value) =>
              isSame(currentTask, getQueueItemId(value))
            );
            if (update) {
              const updatedTask = {
                ...currentTask,
                ...update,
                $storedLocally: false,
              };

              const taskId = getQueueItemId(updatedTask);
              const existingForm = state.taskForms[taskId];

              console.log('[Redux AI Update] Checking for AI data:', {
                taskId,
                hasExistingForm: !!existingForm,
                hasAISummary: !!update.ai_case_summary,
                hasAIMetaFields: !!update.ai_meta_fields,
                aiSummaryType: typeof update.ai_case_summary,
                aiMetaFieldsType: typeof update.ai_meta_fields,
              });

              const aiData = update.ai_meta_fields || update.ai_case_summary;

              if (existingForm && aiData && typeof aiData === 'object') {
                const aiFields = aiData as any;
                console.log('[Redux AI Update] AI data content:', {
                  species: aiFields.species,
                  fieldsCount: Object.keys(aiFields).length,
                  petName: aiFields.pet_name || aiFields.petName,
                  clientName: aiFields.client_name || aiFields.clientName,
                  source: update.ai_meta_fields
                    ? 'ai_meta_fields'
                    : 'ai_case_summary',
                  hasChiefComplaints: !!(
                    aiFields.chief_complaints || aiFields.chiefComplaint
                  ),
                  problemSummary: (
                    aiFields.problem_summary || aiFields.problemSummary
                  )?.substring(0, 50),
                });

                const updates: Record<string, any> = {};

                if (
                  (aiFields.chief_complaints || aiFields.chiefComplaints) &&
                  !existingForm.chief_complaints?.length
                ) {
                  updates.chief_complaints =
                    aiFields.chief_complaints || aiFields.chiefComplaints;
                }
                if (
                  aiFields.chiefComplaint &&
                  !existingForm.chief_complaints?.length
                ) {
                  const existingComplaints = updates.chief_complaints || [];
                  if (!existingComplaints.includes(aiFields.chiefComplaint)) {
                    updates.chief_complaints = [
                      ...existingComplaints,
                      aiFields.chiefComplaint,
                    ];
                  }
                }
                if (
                  (aiFields.problem_summary || aiFields.problemSummary) &&
                  !existingForm.problem_summary
                ) {
                  updates.problem_summary =
                    aiFields.problem_summary || aiFields.problemSummary;
                }
                if (
                  (aiFields.internal_notes || aiFields.internalNotes) &&
                  !existingForm.internal_notes
                ) {
                  updates.internal_notes =
                    aiFields.internal_notes || aiFields.internalNotes;
                }
                if (
                  (aiFields.additional_notes || aiFields.additionalNotes) &&
                  !existingForm.additional_notes
                ) {
                  updates.additional_notes =
                    aiFields.additional_notes || aiFields.additionalNotes;
                }
                if (aiFields.signalment && !existingForm.signalment) {
                  updates.signalment = aiFields.signalment;
                }
                if (aiFields.medications && !existingForm.medications) {
                  updates.medications = aiFields.medications;
                }
                if (aiFields.diet && !existingForm.diet) {
                  updates.diet = aiFields.diet;
                }

                if (
                  (aiFields.client_name || aiFields.clientName) &&
                  (aiFields.client_name || aiFields.clientName) !== 'Unknown' &&
                  !existingForm.client_name
                ) {
                  updates.client_name =
                    aiFields.client_name || aiFields.clientName;
                }
                if (
                  (aiFields.client_email || aiFields.clientEmail) &&
                  (aiFields.client_email || aiFields.clientEmail) !==
                    'Unknown' &&
                  !existingForm.client_email
                ) {
                  updates.client_email =
                    aiFields.client_email || aiFields.clientEmail;
                }
                if (
                  (aiFields.client_phone || aiFields.clientPhone) &&
                  (aiFields.client_phone || aiFields.clientPhone) !==
                    'Unknown' &&
                  !existingForm.client_phone
                ) {
                  updates.client_phone =
                    aiFields.client_phone || aiFields.clientPhone;
                }
                if (
                  (aiFields.pet_name || aiFields.petName) &&
                  (aiFields.pet_name || aiFields.petName) !== 'Unknown' &&
                  !existingForm.pet_name
                ) {
                  updates.pet_name = aiFields.pet_name || aiFields.petName;
                }
                if (
                  aiFields.species &&
                  aiFields.species !== 'Unknown' &&
                  !existingForm.species
                ) {
                  updates.species = aiFields.species;
                }
                if (
                  aiFields.breed &&
                  aiFields.breed !== 'Unknown' &&
                  !existingForm.breed
                ) {
                  updates.breed = aiFields.breed;
                }
                if (aiFields.age && !existingForm.age) {
                  updates.age = aiFields.age;
                }
                if (aiFields.sex && !existingForm.sex) {
                  updates.sex = aiFields.sex;
                }
                if (aiFields.weight && !existingForm.weight) {
                  updates.weight = aiFields.weight;
                }

                // Medical status
                if (aiFields.appetite && !existingForm.appetite) {
                  updates.appetite = aiFields.appetite;
                }
                if (aiFields.thirst && !existingForm.thirst) {
                  updates.thirst = aiFields.thirst;
                }
                if (aiFields.urination && !existingForm.urination) {
                  updates.urination = aiFields.urination;
                }
                if (aiFields.defecation && !existingForm.defecation) {
                  updates.defecation = aiFields.defecation;
                }
                if (aiFields.vomiting && !existingForm.vomiting) {
                  updates.vomiting = aiFields.vomiting;
                }
                if (aiFields.diarrhea && !existingForm.diarrhea) {
                  updates.diarrhea = aiFields.diarrhea;
                }
                if (aiFields.allergies && !existingForm.allergies) {
                  updates.allergies = aiFields.allergies;
                }

                // Other fields
                if (aiFields.duration && !existingForm.duration) {
                  updates.duration = aiFields.duration;
                }
                if (
                  (aiFields.symptoms_description ||
                    aiFields.symptomsDescription) &&
                  !existingForm.symptoms_description
                ) {
                  updates.symptoms_description =
                    aiFields.symptoms_description ||
                    aiFields.symptomsDescription;
                }
                if (
                  (aiFields.urgency_level || aiFields.urgencyLevel) &&
                  !existingForm.urgency_level
                ) {
                  updates.urgency_level =
                    aiFields.urgency_level || aiFields.urgencyLevel;
                }

                // Apply updates to form
                if (Object.keys(updates).length > 0) {
                  state.taskForms[taskId] = {
                    ...existingForm,
                    ...updates,
                  };
                  console.log(
                    '[AI Summary] Auto-populated form fields:',
                    taskId,
                    updates
                  );
                }
              }

              return updatedTask;
            } else {
              const newTaskInfo = {
                ...currentTask,
                connected: false,
                initializing: false,
                $storedLocally: true,
                isConnectedByMe: false,
              };
              if (!newTaskInfo.$finished) {
                newTaskInfo.$finished = Date.now();
              }
              return newTaskInfo;
            }
          });
        }

        if (receiverId) {
          state.currentTasks.push(
            ...payload.filter(
              (task) =>
                task.type === ActionQueueType.SMS &&
                task.connected &&
                task.receiver_id.includes(receiverId) &&
                !state.currentTasks.find((value) =>
                  isSame(task, getQueueItemId(value))
                )
            )
          );
        }

        logQueueChanges(state);
      }
    );
    builder.addMatcher(isLogoutAction, (state) => {
      actionQueueAdapter.removeAll(state);
      logQueuePayload(state, []);
      logQueueChanges(state);
      state.currentTasks = [];
      state.currentTaskId = undefined;
      state.taskForms = {};
    });
  },
  reducers: {
    removeTransferCallForm: (state) => {
      state.transferCallForm = undefined;
    },
    removeTaskForResponse: (state) => {
      state.taskIdForResponse = undefined;
    },
    setCurrentTaskId: (state, { payload }) => {
      state.currentTaskId = payload;
    },
    setIsTaskSwitching: (state, { payload }: PayloadAction<boolean>) => {
      state.isTaskSwitching = payload;
    },
    addTransferCallForm: (
      state,
      { payload }: PayloadAction<TransferMergeFormValues>
    ) => {
      state.transferCallForm = payload;
    },
    updateCallFlowForm: (
      state,
      { payload }: PayloadAction<UpdateCallFlowFormPayload>
    ) => {
      const { task, form } = payload;
      const id = actionQueueAdapter.selectId(task);

      state.callFlowForms[id] = form;
    },
    setTaskIsLoading: (
      state,
      { payload }: PayloadAction<TaskIsLoadingProps>
    ) => {
      const { task, isLoading } = payload;
      const current = new Set(state.loadingTasks);
      const id = getQueueItemId(task);
      if (isLoading) {
        current.add(id);
      } else {
        current.delete(id);
      }
      state.loadingTasks = Array.from(current);
    },
    setClientInProgress: (
      state,
      {
        payload: { task, inProgress },
      }: PayloadAction<SetClientInProgressUpdate>
    ) => {
      const id = getQueueItemId(task);
      const set = new Set(state.clientSetInProgress);
      if (inProgress) {
        set.add(id);
      } else {
        set.delete(id);
      }
      state.clientSetInProgress = Array.from(set);
    },
    setTaskOnHold: (state, { payload }: PayloadAction<SetOnHoldAction>) => {
      const { currentTasks } = state;
      const index = currentTasks.findIndex((value) =>
        isSame(payload.task, getQueueItemId(value))
      );
      if (index >= 0) {
        state.currentTasks[index] = {
          ...state.currentTasks[index],
          $isOnHold: payload.isOnHold,
        };
        logQueueChanges(state);
      }
    },
    updateForm: (state, { payload }: PayloadAction<UpdateCaseForm>) => {
      const { task, form } = payload;
      const id = actionQueueAdapter.selectId(task);
      const filteredForm: Record<string, any> = {};
      Object.entries(form).forEach(([key, value]) => {
        if (value || booleanFormFields.includes(key)) {
          filteredForm[key] = value;
        }
      });
      console.log('update form', id);
      state.taskForms[id] = {
        ...state.taskForms[id],
        ...filteredForm,
      };
    },
    addTask: (state, { payload }: PayloadAction<AddTask>) => {
      const { task, form } = payload;
      let { receiverId, currentTasks, taskIdForResponse } = state;
      const isVoice = task.type === ActionQueueType.Voice;
      const id = getQueueItemId(task);
      const index = currentTasks.findIndex((value) =>
        isSame(task, getQueueItemId(value))
      );
      console.log('add task', id, task.CSID);
      state.currentTaskId = id;
      state.isTaskSwitching = false;

      if (form) {
        state.taskForms[id] = fillSavedFormIfExist(form, id, receiverId);
      } else {
        const currentForm = state.taskForms[id];
        if (currentForm) {
          const loadedForm = fillSavedFormIfExist(
            state.taskForms[id],
            id,
            receiverId
          );
          state.taskForms[id] = loadedForm;
        }
      }
      if (!state.callFlowForms[id] && isVoice) {
        state.callFlowForms[id] = { opened: [], checked: {} };
      }

      if (index >= 0) {
        currentTasks[index] = {
          ...currentTasks[index],
          ...payload,
          $finished: undefined,
          $callEndedByUser: false,
        };
      } else {
        currentTasks.push(task);
      }
      state.currentTasks = state.currentTasks.map((currentTask) => {
        if (isSame(currentTask, state.currentTaskId)) {
          return {
            ...currentTask,
            $finished: undefined,
          };
        } else {
          return {
            ...currentTask,
            $finished: Date.now(),
          };
        }
      });
      logQueueChanges(state);
      if (id === taskIdForResponse) {
        state.taskIdForResponse = undefined;
      }
    },
    removeTask: (state, { payload }: PayloadAction<EndTask>) => {
      const { taskForms, currentTasks, callFlowForms, currentTaskId } = state;
      let task: ActionQueue | undefined = undefined;
      let index: number = -1;
      if ('task' in payload) {
        task = payload.task;
        index = currentTasks.findIndex((value) =>
          isSame(payload.task, getQueueItemId(value))
        );
      } else if ('caseId' in payload) {
        index = currentTasks.findIndex(
          (value) => value.case_id === payload.caseId
        );
        task = currentTasks[index];
      }
      if (!task) {
        return;
      }

      const id = actionQueueAdapter.selectId(task);
      const isVoice = task.type === ActionQueueType.Voice;
      let newTaskId: string | undefined;
      console.log('removeTask', id, `${index} of ${currentTasks.length}`);

      if (index >= 0) {
        const { user } = payload;
        currentTasks.splice(index, 1);
        if (currentTaskId && isSame(task, currentTaskId)) {
          if (currentTasks.length && user) {
            const firstTask = currentTasks[0];
            newTaskId = getQueueItemId(firstTask);
            state.currentTaskId = newTaskId;
            state.isTaskSwitching = true;

            if (!taskForms[newTaskId]) {
              state.taskForms[newTaskId] = getActionCenterCaseForm(
                firstTask,
                user
              );
            }
            if (!callFlowForms[newTaskId] && isVoice) {
              state.callFlowForms[newTaskId] = { opened: [], checked: {} };
            }
          } else {
            state.currentTaskId = undefined;
          }
        }
      }
      clearCaseOnCallPopupOpened(id);
      delete state.taskForms[id];
      delete state.callFlowForms[id];
      state.taskIdForResponse = state.currentTaskId;
    },
    updateTask: (state, { payload }: PayloadAction<UpdateTask>) => {
      let index = -1;
      if ('caseId' in payload) {
        index = state.currentTasks.findIndex(
          (value) => value.case_id === payload.caseId
        );
      } else {
        index = state.currentTasks.findIndex((value) =>
          isSame(payload.changes as ActionQueue, getQueueItemId(value))
        );
      }
      if (index >= 0) {
        const updatedTask = {
          ...state.currentTasks[index],
          ...payload.changes,
        };
        state.currentTasks[index] = updatedTask;

        // Update form with AI data if present
        const taskId = getQueueItemId(updatedTask);
        const existingForm = state.taskForms[taskId];
        if (existingForm && payload.changes) {
          const changes = payload.changes as ActionQueue;

          // Check for ai_meta_fields first (takes priority), then ai_case_summary
          const aiData = changes.ai_meta_fields || changes.ai_case_summary;

          // Update form fields from AI data if available
          if (aiData && typeof aiData === 'object') {
            const aiFields = aiData as any;
            const updates: Record<string, any> = {};

            // Only update if fields are not already filled
            // Handle both snake_case (ai_meta_fields) and camelCase (ai_case_summary) field names
            if (
              (aiFields.chief_complaints || aiFields.chiefComplaints) &&
              !existingForm.chief_complaints?.length
            ) {
              updates.chief_complaints =
                aiFields.chief_complaints || aiFields.chiefComplaints;
            }
            if (
              aiFields.chiefComplaint &&
              !existingForm.chief_complaints?.length
            ) {
              const existingComplaints = updates.chief_complaints || [];
              // Only add if it's not already in the array
              if (!existingComplaints.includes(aiFields.chiefComplaint)) {
                updates.chief_complaints = [
                  ...existingComplaints,
                  aiFields.chiefComplaint,
                ];
              }
            }
            if (
              (aiFields.problem_summary || aiFields.problemSummary) &&
              !existingForm.problem_summary
            ) {
              updates.problem_summary =
                aiFields.problem_summary || aiFields.problemSummary;
            }
            if (
              (aiFields.internal_notes || aiFields.internalNotes) &&
              !existingForm.internal_notes
            ) {
              updates.internal_notes =
                aiFields.internal_notes || aiFields.internalNotes;
            }
            if (
              (aiFields.additional_notes || aiFields.additionalNotes) &&
              !existingForm.additional_notes
            ) {
              updates.additional_notes =
                aiFields.additional_notes || aiFields.additionalNotes;
            }
            if (aiFields.signalment && !existingForm.signalment) {
              updates.signalment = aiFields.signalment;
            }
            if (aiFields.medications && !existingForm.medications) {
              updates.medications = aiFields.medications;
            }
            if (aiFields.diet && !existingForm.diet) {
              updates.diet = aiFields.diet;
            }

            // Update client and pet information
            if (
              (aiFields.client_name || aiFields.clientName) &&
              (aiFields.client_name || aiFields.clientName) !== 'Unknown' &&
              !existingForm.client_name
            ) {
              updates.client_name = aiFields.client_name || aiFields.clientName;
            }
            if (
              (aiFields.pet_name || aiFields.petName) &&
              (aiFields.pet_name || aiFields.petName) !== 'Unknown' &&
              !existingForm.pet_name
            ) {
              updates.pet_name = aiFields.pet_name || aiFields.petName;
            }
            if (
              aiFields.species &&
              aiFields.species !== 'Unknown' &&
              !existingForm.species
            ) {
              updates.species = aiFields.species;
            }
            if (aiFields.duration && !existingForm.duration) {
              updates.duration = aiFields.duration;
            }
            if (
              (aiFields.symptoms_description || aiFields.symptomsDescription) &&
              !existingForm.symptoms_description
            ) {
              updates.symptoms_description =
                aiFields.symptoms_description || aiFields.symptomsDescription;
            }
            if (
              (aiFields.urgency_level || aiFields.urgencyLevel) &&
              !existingForm.urgency_level
            ) {
              updates.urgency_level =
                aiFields.urgency_level || aiFields.urgencyLevel;
            }

            // Apply updates to form
            if (Object.keys(updates).length > 0) {
              state.taskForms[taskId] = {
                ...existingForm,
                ...updates,
              };
              console.log('[AI Summary] Updated form fields:', taskId, updates);
            }
          }
        }

        logQueueChanges(state);
      }
    },
  },
});

export const { useListQuery, useLazyListQuery } = actionQueueApi;

const {
  actions: {
    addTask,
    removeTask,
    updateForm,
    updateTask,
    setTaskOnHold,
    setCurrentTaskId,
    setTaskIsLoading,
    setIsTaskSwitching,
    updateCallFlowForm,
    addTransferCallForm,
    setClientInProgress,
    removeTaskForResponse,
    removeTransferCallForm,
  },
} = actionQueueSlice;

export {
  addTask,
  updateForm,
  removeTask,
  updateTask,
  setTaskOnHold,
  setTaskIsLoading,
  PetOwnerTemplate,
  setCurrentTaskId,
  setIsTaskSwitching,
  updateCallFlowForm,
  addTransferCallForm,
  setClientInProgress,
  MessageRecipientType,
  removeTaskForResponse,
  removeTransferCallForm,
};

export type { SMSBatchPayload };
